/**
 * Which sign-in screen a signed-out visitor sees — decided once, here.
 *
 * Neither screen has an address. RequireAuth shows the chosen one at the
 * landing URL ('/'; '/cca/' or '/eia/' locally), moving a signed-out visitor
 * there from any other path, so the address bar never shows a sign-in path or a
 * protected one. The two screens:
 *   - Advisory (HomePage): a button that hands off to Common Services for SSO.
 *     This is what legacy does on AMD hosts, where the session is created by
 *     Common Services and shared through *.amd.com cookies.
 *   - Local (LocalLoginPage): the email + password form posting to this app's
 *     own /login API. A DEVELOPMENT tool: the shared cookies a Common Services
 *     sign-in produces never reach localhost, so locally it is the only way in.
 *
 * The local form exists on LOCALHOST ONLY:
 *   - On localhost it is the default, and VITE_ADVISORY_LOGIN /
 *     VITE_LOCAL_LOGIN (in .env.local) switch to Advisory for manual testing.
 *   - On ANY other host — AMD production, dev or staging hosts, a LAN address
 *     — it is ALWAYS Advisory, and the overrides are ignored. On purpose: no env
 *     file, flag or build setting can put a credential form in front of real
 *     users and bypass SSO. "Local" is decided by isLocalHost(), the same rule
 *     that decides the /cca | /eia URL prefix (router/basename.js), so the two
 *     can never disagree about what counts as local.
 *
 * Components do not call this. routes.jsx reads it once at module load, as it
 * does for the app mode and basename — host and build env are constant for the
 * life of a page — so only the chosen screen's chunk is ever downloaded.
 */
import { env } from '@config/env';
import { isLocalHost } from '@utils/hosts';

/**
 * Pure decision, so every case is testable without touching `window` or env.
 *
 * `advisoryOverride` / `localOverride` are true / false / null, where null means
 * "no opinion". The distinction matters: `localLogin=false` says "not the local
 * form", which only means "Advisory" because there are exactly two screens.
 *
 * Off localhost: always advisory, before any override is even read.
 *
 * Precedence on localhost, first match wins:
 *   advisory=true            -> advisory   (also wins when both are true)
 *   local=true               -> local
 *   local=false, advisory!=false -> advisory (only "not local" was said)
 *   otherwise                -> local      (the default, and advisory=false)
 *
 * @param {{ hostname?: string,
 *           advisoryOverride?: boolean | null,
 *           localOverride?: boolean | null }} input
 * @returns {'advisory' | 'local'}
 */
export function resolveLoginMode({ hostname, advisoryOverride = null, localOverride = null }) {
  if (!isLocalHost(hostname)) return 'advisory';

  if (advisoryOverride === true) return 'advisory';
  if (localOverride === true) return 'local';
  if (localOverride === false && advisoryOverride !== false) return 'advisory';
  return 'local';
}

/**
 * The two switches, resolved. Exactly one is true.
 *
 * Returned as a pair rather than a single mode string because that is how the
 * choice is talked about ("advisoryLogin: true, localLogin: false"), and a
 * boolean pair reads naturally at the one call site.
 *
 * @returns {{ advisoryLogin: boolean, localLogin: boolean }}
 */
export function getLoginFlags() {
  const mode = resolveLoginMode({
    hostname: typeof window === 'undefined' ? '' : window.location.hostname,
    advisoryOverride: env.advisoryLoginOverride,
    localOverride: env.localLoginOverride,
  });

  return { advisoryLogin: mode === 'advisory', localLogin: mode === 'local' };
}
